import { ActiveSubscriptionsModel } from 'src/app/models/activated-subscription.model';
import { FeaturesListModel, SubscriptionModel } from 'src/app/models/subscription-model';
import { CreditQuotaHelper } from '../../constants/credit-quota.constants';

/**
 * Pure helpers for subscription plans: no HTTP and no subscriptions, so they
 * are cheap to call and easy to unit test. The API decides what is sold; these
 * functions only interpret the plan list it returns.
 */

const TIER_RANK: Record<string, number> = { tier0: 0, tier1: 1, tier2: 2 };
const FREQUENCY_RANK: Record<string, number> = { monthly: 0, annual: 1 };
const CREDIT_FEATURE = /kits.*credits/i;

type ActiveSubscription = ActiveSubscriptionsModel & { createdAt?: string };

export interface ResolvedActivePlan {
  plan: SubscriptionModel;
  subscription: ActiveSubscription;
}

/** Static presentation data from subs-data.json, attached to API plans by tier + commission type. */
export interface PlanOverlay {
  id?: string;
  plan_name?: string;
  tier?: string;
  /** billing periods on sale for this tier + commission group; [] = shown only to existing subscribers */
  frequency?: string[];
  isCommission?: boolean;
  order?: number;
  className?: string;
  backgroundColor?: string;
  royaltyDetails?: string;
  isShowRoyaltyDetails?: boolean;
  isShowButton?: boolean;
  isShowRecommendOption?: boolean;
  discountBpmOffer?: any;
  compareFeaturesList?: Array<Partial<FeaturesListModel>>;
  featuresList?: Array<Partial<FeaturesListModel> & { labelByFrequency?: Record<string, string> }>;
}

/** Commission read defensively: some old rows omit it or carry a mis-keyed field. */
export function getCommissionPercent(plan: Partial<SubscriptionModel>): number {
  const value = Number(plan?.sellerCommissionPercent);
  return Number.isFinite(value) ? value : 0;
}

/** tier + commission type, e.g. "tier2:commission". Used instead of plan names. */
export function getPlanGroupKey(plan: Partial<SubscriptionModel>): string {
  return `${plan.tier}:${getCommissionPercent(plan) > 0 ? 'commission' : 'free'}`;
}

/** Plan can be bought at all: enabled in the API and flagged for the frontend. */
export function isSellablePlan(plan: Partial<SubscriptionModel>): boolean {
  return plan.active !== false && plan.showOnFrontend === true;
}

function findOverlay(plan: Partial<SubscriptionModel>, overlays: PlanOverlay[]): PlanOverlay | undefined {
  const isCommission = getCommissionPercent(plan) > 0;
  return overlays.find(item => !!item.tier && item.tier === plan.tier && !!item.isCommission === isCommission);
}

/** Within one slot the current-generation plan (has credits) wins, then the one listed last by the API. */
function isNewerForSlot(candidate: SubscriptionModel, current: SubscriptionModel): boolean {
  const candidateNew = (candidate.creditsPerCycle ?? 0) > 0;
  const currentNew = (current.creditsPerCycle ?? 0) > 0;
  return candidateNew || !currentNew;
}

/**
 * Plans the pricing page lists: sellable plans whose billing period is on sale
 * for their group (subs-data.json `frequency`), one card per
 * tier / commission type / frequency.
 */
export function getSellablePlans(catalog: SubscriptionModel[], overlays: PlanOverlay[]): SubscriptionModel[] {
  const slots = new Map<string, SubscriptionModel>();
  catalog.forEach(plan => {
    const onSale = findOverlay(plan, overlays)?.frequency?.includes(plan.frequency);
    if (!isSellablePlan(plan) || !onSale) {
      return;
    }
    const key = `${getPlanGroupKey(plan)}:${plan.frequency}`;
    const current = slots.get(key);
    if (!current || isNewerForSlot(plan, current)) {
      slots.set(key, plan);
    }
  });
  return Array.from(slots.values());
}

/**
 * Finds the user's current plan in the FULL catalog (old plans included) by
 * razorpay plan id. Several active subscriptions: highest tier wins, then the
 * most recently created.
 */
export function resolveActivePlan(
  catalog: SubscriptionModel[],
  activeSubscriptions: ActiveSubscription[] = []
): ResolvedActivePlan | null {
  const plansById = new Map<string, SubscriptionModel>();
  catalog.forEach(plan => plan.razorpayPlanId && plansById.set(plan.razorpayPlanId, plan));

  let best: ResolvedActivePlan | null = null;
  for (const subscription of activeSubscriptions) {
    const plan = plansById.get(subscription.planId);
    if (!plan) {
      continue;
    }
    if (!best || isPreferred(plan, subscription, best)) {
      best = { plan, subscription };
    }
  }
  return best;
}

function isPreferred(plan: SubscriptionModel, subscription: ActiveSubscription, current: ResolvedActivePlan): boolean {
  const tierDiff = (TIER_RANK[plan.tier ?? ''] ?? 0) - (TIER_RANK[current.plan.tier ?? ''] ?? 0);
  if (tierDiff !== 0) {
    return tierDiff > 0;
  }
  return (Date.parse(subscription.createdAt ?? '') || 0) > (Date.parse(current.subscription.createdAt ?? '') || 0);
}

/**
 * Plans to show for a user with an active subscription:
 * - an old plan that is no longer listed is added (flagged isLegacy) so it can
 *   still be managed / cancelled; it takes its slot, so a slot never shows two cards
 * - within the user's own tier + commission group, shorter billing periods are
 *   hidden (an annual subscriber is not offered the monthly plan of that group)
 */
export function withLegacyActivePlan(sellable: SubscriptionModel[], active: ResolvedActivePlan | null): SubscriptionModel[] {
  if (!active) {
    return sellable;
  }
  const group = getPlanGroupKey(active.plan);
  const activeRank = FREQUENCY_RANK[active.plan.frequency] ?? 0;
  const isListed = sellable.some(plan => plan.razorpayPlanId === active.plan.razorpayPlanId);

  const plans = sellable.filter(plan => {
    if (getPlanGroupKey(plan) !== group || plan.razorpayPlanId === active.plan.razorpayPlanId) {
      return true;
    }
    const sameSlot = plan.frequency === active.plan.frequency;
    const isDowngrade = (FREQUENCY_RANK[plan.frequency] ?? 0) < activeRank;
    return !isDowngrade && !(sameSlot && !isListed);
  });

  if (!isListed) {
    plans.push({ ...active.plan, isLegacy: true });
  }
  return plans.length === sellable.length && isListed ? sellable : plans;
}

/** Credits for a plan: API value, or the legacy quota table when the API sends 0 (old plans). */
export function getPlanCredits(plan: Partial<SubscriptionModel> | null | undefined, legacyQuota: number): number {
  return (plan?.creditsPerCycle ?? 0) > 0 ? plan!.creditsPerCycle! : legacyQuota;
}

/**
 * Copies overlay features, guaranteeing isFeatureAvailable is a boolean (the JSON may omit it).
 * A feature's `labelByFrequency` (e.g. { monthly, annual }) overrides its label for that billing period.
 */
function toFeatureList(
  features: Array<Partial<FeaturesListModel> & { labelByFrequency?: Record<string, string> }> | undefined,
  frequency?: string
): FeaturesListModel[] {
  return (features ?? []).map(({ labelByFrequency, ...feature }) => ({
    ...feature,
    id: feature.id ?? '',
    label: (frequency && labelByFrequency?.[frequency]) || feature.label || '',
    isFeatureAvailable: !!feature.isFeatureAvailable
  }));
}

/**
 * Feature list with numbers taken from the plan (credits, ad wallet) instead of
 * hard-coded labels. Old plans (creditsPerCycle 0) use the legacy quota table.
 */
function buildFeatures(plan: SubscriptionModel, overlay: PlanOverlay): FeaturesListModel[] {
  const features = toFeatureList(overlay.featuresList, plan.frequency);
  const legacyQuota = CreditQuotaHelper.getQuota(plan.tier ?? '', plan.frequency, getCommissionPercent(plan) > 0);
  const credits = getPlanCredits(plan, legacyQuota);
  const adWallet = plan.adWalletGrant ?? 0;
  if (credits > 0) {
    features.forEach(feature => {
      if (CREDIT_FEATURE.test(feature.label)) {
        feature.label = feature.label.replace(/^\d+/, String(credits));
      }
    });
  }
  // if (adWallet > 0) {
  //   const moreIndex = features.findIndex(feature => feature.label === 'more');
  //   features.splice(moreIndex === -1 ? features.length : moreIndex, 0, {
  //     id: 'feature_ad_wallet',
  //     label: `₹${adWallet} Campaign Ad Credits`,
  //     isFeatureAvailable: true
  //   });
  // }
  return features;
}

/** Enriches one API plan with presentation data. Plans without a matching overlay are returned unchanged. */
export function applyOverlay(plan: SubscriptionModel, overlays: PlanOverlay[]): SubscriptionModel {
  const commission = getCommissionPercent(plan);
  const isCommission = commission > 0;
  const overlay = findOverlay(plan, overlays);
  if (!overlay) {
    return plan;
  }
  return {
    ...plan,
    sellerCommissionPercent: commission,
    offerText: `${commission}% Platform Commission`,
    planName: `${overlay.plan_name?.split(' ')[0] ?? plan.planName} Plan`,
    isShowRoyaltyDetails: overlay.isShowRoyaltyDetails,
    royaltyDetails: overlay.royaltyDetails,
    isShowButton: overlay.isShowButton,
    backgroundColor: overlay.backgroundColor,
    order: overlay.order,
    isCommission,
    compareFeaturesList: toFeatureList(overlay.compareFeaturesList),
    featuresList: buildFeatures(plan, overlay),
    discountBpmOffer: overlay.discountBpmOffer,
    isShowRecommendOption: overlay.isShowRecommendOption,
    isShowFounderOption: false,
    isExpandPlan: false,
    className: overlay.className,
    id: overlay.id,
    isActive: false,
    isSelected: false,
    isShowPlan: true
  };
}

/** Every API plan (old ones included) enriched with presentation data. */
export function buildCatalog(apiPlans: SubscriptionModel[], overlays: PlanOverlay[]): SubscriptionModel[] {
  return apiPlans.map(plan => applyOverlay(plan, overlays));
}
